import React, { FunctionComponent, useCallback, useMemo } from "react";
import { InputAdornment, TextField, Theme } from "@material-ui/core";
import { InputProps } from "@material-ui/core/Input/Input";
import makeStyles from "@material-ui/core/styles/makeStyles";
import FilterListIcon from "@material-ui/icons/FilterList";
import CloseIcon from "@material-ui/icons/Close";
import Chip from "@material-ui/core/Chip";
import CalendarTodayOutlinedIcon from "@material-ui/icons/CalendarTodayOutlined";
import classnames from "classnames";
import { SHIPMENT_REVIEW } from "constants/integration/shipment-review";
import SearchIcon from "components/icons/magnify-glass";
import { formatMessage } from "lib/globalize-util";
import { theme } from "styles/theme";
import { useDebouncedHandler } from "hooks";
import { VIEW_MODE } from "types/cart/shipment-review";

const useStyles = makeStyles({
    cartTopBarWrap: {
        display: "flex",
        justifyContent: "right",
        alignItems: "center",
        width: "100%",
        paddingRight: "24px",
        "& > *": {
            marginLeft: "24px",
        },
    },
    clearButton: {
        color: theme.palette.darkGrey,
        backgroundColor: theme.palette.borderColor,
        height: "25px",
        textTransform: "none",
        fontSize: "14px",
        overflow: "hidden",
        minWidth: 65,
    },
    filterIcon: {
        color: theme.palette.accent4Color + " !important",
        width: "14px !important",
        height: "14px !important",
    },
    search: {
        "& input": {
            border: 0,
            fontSize: "14px",
            paddingLeft: "2px",
        },
        "& .MuiInput-underline:after": {
            borderBottomColor: theme.palette.primaryHover,
        },
    },
    icon: {
        height: "18px !important",
    },
    closeIcon: {
        cursor: "pointer",
    },
});

export interface IProps {
    searchText: string;
    viewMode: VIEW_MODE;
    onChangeSearch: (searchText: string) => void;
    onChangeDeliveryGrouping: () => void;
    onClearFilterAndSearch: () => void;
    showResetFilters: boolean;
}

type ChangeEvent = Parameters<React.ComponentProps<typeof TextField>["onChange"]>[0];

export const CartTopBar: FunctionComponent<IProps> = function (props: IProps) {
    const { viewMode, searchText, showResetFilters } = props;
    const { onChangeDeliveryGrouping, onChangeSearch, onClearFilterAndSearch } = props;
    const classes = useStyles();

    const [searchValue, handleSearchChange] = useDebouncedHandler({
        value: searchText,
        onEvent: onChangeSearch,
        getEventValue: (e: ChangeEvent) => e.target.value,
    });

    const onClearSearch = useCallback((): void => {
        onChangeSearch("");
    }, [onChangeSearch]);

    const resetFiltersAndSort = useCallback((): void => {
        onChangeSearch("");
        onClearFilterAndSearch();
    }, [onClearFilterAndSearch, onChangeSearch]);

    const chipClasses = useMemo(() => ({ label: "chipLabel" }), []);
    const chipIcon = useMemo((): JSX.Element => <FilterListIcon className={classes.filterIcon} />, [classes]);
    const inputProps = useMemo(
        (): InputProps => ({
            startAdornment: (
                <InputAdornment position="start">
                    <SearchIcon className={classes.icon} color={theme.palette.darkGrey} />
                </InputAdornment>
            ),
            endAdornment: (
                <InputAdornment position="end">
                    <CloseIcon
                        className={classnames(classes.icon, classes.closeIcon, { hidden: !searchValue })}
                        onClick={onClearSearch}
                        data-test={SHIPMENT_REVIEW.CLEAR_SEARCH}
                    />
                </InputAdornment>
            ),
        }),
        [classes, searchValue, onClearSearch]
    );

    return (
        <div className={classes.cartTopBarWrap}>
            <Chip
                className={classnames(classes.clearButton, { hidden: !(searchText || showResetFilters) })}
                classes={chipClasses}
                label={formatMessage("Reset Grid view")}
                icon={chipIcon}
                onClick={resetFiltersAndSort}
                onDelete={resetFiltersAndSort}
                data-test={SHIPMENT_REVIEW.RESET_GRID_VIEW}
            />
            <IconAction
                active={viewMode === VIEW_MODE.SHIPMENT}
                title={formatMessage("Deliveries")}
                dataTest={SHIPMENT_REVIEW.SHIPMENT_MODE_ICON}
                icon={
                    <CalendarTodayOutlinedIcon
                        htmlColor={
                            viewMode === VIEW_MODE.SHIPMENT ? theme.palette.primary1Color : theme.palette.darkGrey
                        }
                    />
                }
                onClick={onChangeDeliveryGrouping}
            />
            <TextField
                className={classes.search}
                placeholder={formatMessage("Search in cart")}
                onChange={handleSearchChange}
                value={searchValue}
                variant="standard"
                size="small"
                InputProps={inputProps}
                data-test={SHIPMENT_REVIEW.SEARCH_FILED}
            />
        </div>
    );
};

const IconAction = (props) => {
    const { title, active, icon, dataTest = "", onClick } = props;
    const classes = useIconFilterStyles({ active });
    return (
        <div className={classes.wrapper} onClick={onClick} data-test={dataTest}>
            {icon}
            <div className={classes.title}>{title}</div>
        </div>
    );
};

const useIconFilterStyles = makeStyles<Theme, { active: boolean }>({
    wrapper: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
    },
    title: ({ active }) => ({
        fontSize: 10,
        marginTop: 5,
        color: active ? theme.palette.primary1Color : theme.palette.darkGrey,
        fontWeight: "bold",
    }),
});
